import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import "jspdf-autotable";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import ClassTimingEditor from "../components/ClassTimingEditor";
import { validateSchedule } from "../utils/classTimeUtils";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import PaginationFooter from "../components/PaginationFooter";
import {
  DEFAULT_PAGE_SIZE,
  normalizeListResponse,
} from "../utils/listPagination";
import * as coursesApi from "../api/courses";
import * as sessionsApi from "../api/sessions";
import * as subjectsApi from "../api/subjects";
import * as gradesApi from "../api/grades";
import * as teachersApi from "../api/teachers";

export default function AdminCourses() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } =
    useViewPermission("view_courses");
  const { hasPermission: canEdit } = useViewPermission("edit_courses");
  const { busy, run } = useMutationLock();

  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [scheduleGroups, setScheduleGroups] = useState([
    {
      days: [],
      startTime: "",
      endTime: "",
    },
  ]);

  const [course, setCourse] = useState({
    sessionId: "",
    year: new Date().getFullYear(),
    teacherId: "",
    fee: "",
    duration: "",
    title: "",
    active: "true",
    publicListed: true,
    attendanceEnabled: false,
    classTime: "",
    startDate: "",
  });

  const [availableSessions, setAvailableSessions] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableGrades, setAvailableGrades] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);

  useEffect(() => {
    if (!hasPermission) return;
    const fetchData = async () => {
      try {
        const response = await sessionsApi.list({ exportMode: true });
        setSessions(normalizeListResponse(response.data).data);
        const response2 = await subjectsApi.list({ exportMode: true });
        setSubjects(normalizeListResponse(response2.data).data);
        const response3 = await gradesApi.list({ exportMode: true });
        setGrades(normalizeListResponse(response3.data).data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoading(false);
      }
    };
    fetchData();
  }, [hasPermission]);

  useEffect(() => {
    setAvailableSessions(
      sessions.map((s) => ({
        label: s.startMonth + "-" + s.endMonth,
        value: s.id,
      })),
    );
  }, [sessions]);

  useEffect(() => {
    setAvailableSubjects(
      subjects.map((s) => ({
        label: s.name,
        value: s.name,
      })),
    );
  }, [subjects]);

  useEffect(() => {
    setAvailableGrades(
      grades.map((s) => ({
        label: s.name,
        value: s.name,
      })),
    );
  }, [grades]);

  const [availableDurations] = useState([
    { label: "1 month", value: "1 month" },
    { label: "2 months", value: "2 months" },
    { label: "3 months", value: "3 months" },
    { label: "4 months", value: "4 months" },
    { label: "5 months", value: "5 months" },
    { label: "6 months", value: "6 months" },
    { label: "7 months", value: "7 months" },
    { label: "8 months", value: "8 months" },
    { label: "9 months", value: "9 months" },
    { label: "10 months", value: "10 months" },
    { label: "11 months", value: "11 months" },
    { label: "12 months", value: "12 months" },
  ]);

  const [showform, setshowform] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCourse({ ...course, [name]: value });
  };
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editStates, setEditStates] = useState({});

  // Server-side pagination state for the main courses table
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const limit = DEFAULT_PAGE_SIZE;

  // Filtering logic (sent to the API as query params)
  const [courseFilters, setCourseFilters] = useState({
    teacher: "",
    active: "",
    publicListed: "",
  });

  const fetchCourses = async (pageArg = currentPage, filtersArg = courseFilters) => {
    try {
      const response = await coursesApi.list({
        page: pageArg,
        limit,
        filters: {
          teacherName: filtersArg.teacher,
          active: filtersArg.active,
          publicListed: filtersArg.publicListed,
        },
      });
      const normalized = normalizeListResponse(response.data, pageArg, limit);
      setCourses(normalized.data);
      setCurrentPage(normalized.page);
      setTotal(normalized.total);
      setTotalPages(normalized.totalPages);
    } catch (err) {
      console.error("Error fetching courses:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    await run(async () => {
      try {
        const scheduleError = validateSchedule(scheduleGroups);

        if (scheduleError) {
          notificationCtx.show({
            message: scheduleError,
            stat: "error",
          });

          return;
        }

        const updatedCourse = {
          ...course,
          title: selectedGrade + " " + selectedSubject,
          classTime: scheduleGroups,
          startDate: new Date(course.startDate).toISOString(),
        };
        await coursesApi.create(updatedCourse);
        notificationCtx.show({
          message: "Course added successfully",
          stat: "success",
        });
        setshowform(false);
        setCourse({
          sessionId: "",
          year: new Date().getFullYear(),
          teacherId: "",
          fee: "",
          duration: "",
          title: "",
          active: "true",
          publicListed: true,
          attendanceEnabled: false,
          classTime: "",
          startDate: "",
        });
        setSelectedGrade("");
        setSelectedSubject("");
        setScheduleGroups([{ days: [], startTime: "", endTime: "" }]);
        setCurrentPage(1);
        await fetchCourses(1);
      } catch (error) {
        console.error("Error adding course:", error);
        notificationCtx.show({
          message: "Failed to add the course",
          stat: "error",
        });
      }
    });
  };

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const response = await teachersApi.list({ exportMode: true });
        setTeachers(normalizeListResponse(response.data).data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching teachers:", err);
        setLoading(false);
      }
    };

    if (!hasPermission) return;
    fetchCourses(1);
    fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  const handleInputChange = (id, field, value) => {
    if (!canEdit) return;
    setCourses((prevCourses) =>
      prevCourses.map((course) =>
        course.id === id ? { ...course, [field]: value } : course,
      ),
    );

    setEditStates((prevStates) => ({
      ...prevStates,
      [`${id}-${field}`]: true,
    }));
  };

  // Auto-resize textarea so the full content is visible by default
  const autoResizeTextarea = (textarea) => {
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useEffect(() => {
    const textareas = document.querySelectorAll("textarea[data-autoresize]");
    textareas.forEach(autoResizeTextarea);
  }, [courses]);

  const handleEdit = async (id, field, value) => {
    if (!canEdit) return;
    await run(async () => {
      try {
        await coursesApi.update(id, { field, value });
        setCourses((prevCourses) =>
          prevCourses.map((course) =>
            course.id === id ? { ...course, [field]: value } : course,
          ),
        );
        let message = "Course updated successfully";
        if (field === "active") {
          message = value
            ? "Course activated successfully. All registrations for this course that were unenrolled are enrolled."
            : "Course deactivated successfully. All registrations for this course that were enrolled/pending are unenrolled.";
        } else if (field === "publicListed") {
          message = value
            ? "Course is now shown on the student register page and class schedules."
            : "Course is now hidden from the student register page and class schedules.";
        } else if (field === "attendanceEnabled") {
          message = value
            ? "Attendance enabled. Existing enrollments were given attendance QR tokens."
            : "Attendance disabled for this course.";
        }
        notificationCtx.show({
          message: message,
          stat: "success",
        });
        setEditStates((prevStates) => ({
          ...prevStates,
          [`${id}-${field}`]: false,
        }));
        // Keep the current page in sync with server-side filters (e.g. active).
        await fetchCourses(currentPage);
      } catch (error) {
        console.error("Error updating course:", error);
        notificationCtx.show({
          message: "Failed to update the course",
          stat: "error",
        });
      }
    });
  };

  const handleCourseFilterChange = (key, value) => {
    const next = { ...courseFilters, [key]: value };
    setCourseFilters(next);
    setCurrentPage(1);
    fetchCourses(1, next);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchCourses(newPage);
    }
  };

  const handleDeleteCourse = (course) => {
    if (!canEdit) return;

    const confirmDeleteCourse = async () => {
      try {
        await coursesApi.remove(course.id);

        const nextPage =
          courses.length <= 1 && currentPage > 1 ? currentPage - 1 : currentPage;
        setCurrentPage(nextPage);
        await fetchCourses(nextPage);
        notificationCtx.show({
          message: "Course deleted successfully",
          stat: "success",
        });
      } catch (error) {
        console.error("Delete failed:", error);
        notificationCtx.show({
          message: "Failed to delete course",
          stat: "error",
        });
      }
    };

    if (!course.active) {
      notificationCtx.show({
        message: "Are you sure you want to delete this course?",
        stat: "info",
        onConfirm: confirmDeleteCourse,
      });
    } else {
      notificationCtx.show({
        message: "You must deactivate the course before deleting.",
        stat: "info",
      });
    }
  };

  const WEEK_DAYS = [
    { label: "Mon", full: "Monday" },
    { label: "Tue", full: "Tuesday" },
    { label: "Wed", full: "Wednesday" },
    { label: "Thu", full: "Thursday" },
    { label: "Fri", full: "Friday" },
    { label: "Sat", full: "Saturday" },
    { label: "Sun", full: "Sunday" },
  ];

  const addScheduleGroup = () => {
    setScheduleGroups((prev) => [
      ...prev,
      {
        days: [],
        startTime: "",
        endTime: "",
      },
    ]);
  };

  const removeScheduleGroup = (index) => {
    setScheduleGroups((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleGroupDay = (groupIndex, day) => {
    setScheduleGroups((prev) =>
      prev.map((group, idx) => {
        if (idx !== groupIndex) return group;

        const updatedDays = group.days.includes(day)
          ? group.days.filter((d) => d !== day)
          : [...group.days, day];

        const ordered = WEEK_DAYS.map((d) => d.label).filter((d) =>
          updatedDays.includes(d),
        );

        return {
          ...group,
          days: ordered,
        };
      }),
    );
  };

  const updateGroupField = (index, field, value) => {
    setScheduleGroups((prev) =>
      prev.map((group, idx) =>
        idx === index
          ? {
              ...group,
              [field]: value,
            }
          : group,
      ),
    );
  };

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  const columnKeys = [
    "Course ID",
    "Title",
    "Session",
    "Year",
    "Teacher",
    "Class Timings",
    "Starting Date",
    "Fees",
    "Duration",
    ...(canEdit ? ["Action"] : []),
  ];

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  return (
    <div className="mb-5">
      <Navbar />
      {isAuthenticated && hasPermission && (
        <div
          className="d-flex flex-column"
          style={{
            minHeight: "0",
            height: "109vh",
            overflow: "hidden",
            padding: "10px 50px 0",
          }}
        >
          {/* ================= TOP CONTROLS ================= */}
          <div
            style={{
              padding: "26px 30px",
              backgroundColor: "#d5d1fd",
              borderRadius: "20px",
              flexShrink: 0,
              zIndex: 10,
            }}
          >
            {/* ================= HEADER ================= */}
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                marginBottom: "22px",
              }}
            >
              <div>
                <h1
                  className="mb-1"
                  style={{
                    // fontSize: "28px",
                    fontWeight: "700",
                    color: "#302d45",
                  }}
                >
                  ALL COURSES
                </h1>

                <small
                  style={{
                    color: "#625d75",
                  }}
                >
                  Manage courses, teachers, schedules and course details
                </small>
              </div>

              {canEdit && (
                <button
                  className="btn"
                  onClick={() => setshowform(true)}
                  style={{
                    height: "38px",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#fff",
                    color: "#403b55",
                    border: "none",
                    padding: "0 15px",
                    borderRadius: "8px",
                    fontWeight: "600",
                    fontSize: "13px",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  + Add Course
                </button>
              )}
            </div>

            {/* ================= FILTERS ================= */}
            <div
              className="row g-3"
              style={{
                borderTop: "1px solid rgba(80, 70, 110, 0.12)",
              }}
            >
              {/* Teacher */}
              <div className="col-md-3">
                <select
                  value={courseFilters.teacher}
                  onChange={(e) =>
                    handleCourseFilterChange("teacher", e.target.value)
                  }
                  className="form-select"
                  style={{
                    border: "none",
                    borderRadius: "9px",
                    padding: "10px 13px",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 6px rgba(48,45,69,0.05)",
                    fontSize: "14px",
                    color: "#403b55",
                  }}
                >
                  <option value="">Filter by Teacher</option>

                  {teachers.map((teacher) => (
                    <option key={teacher.id} value={teacher.name}>
                      {teacher.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Active Status */}
              <div className="col-md-3">
                <select
                  value={courseFilters.active}
                  onChange={(e) =>
                    handleCourseFilterChange("active", e.target.value)
                  }
                  className="form-select"
                  style={{
                    border: "none",
                    borderRadius: "9px",
                    padding: "10px 13px",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 6px rgba(48,45,69,0.05)",
                    fontSize: "14px",
                    color: "#403b55",
                  }}
                >
                  <option value="">Filter by Status</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              {/* Catalog visibility */}
              <div className="col-md-3">
                <select
                  value={courseFilters.publicListed}
                  onChange={(e) =>
                    handleCourseFilterChange("publicListed", e.target.value)
                  }
                  className="form-select"
                  style={{
                    border: "none",
                    borderRadius: "9px",
                    padding: "10px 13px",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 6px rgba(48,45,69,0.05)",
                    fontSize: "14px",
                    color: "#403b55",
                  }}
                >
                  <option value="">Filter by Catalog</option>
                  <option value="true">In catalog</option>
                  <option value="false">Offline only</option>
                </select>
              </div>
            </div>
          </div>

          {/* ================= CONTENT ================= */}

          {/* ================= SPACING ================= */}
          <div style={{ height: "16px", flexShrink: 0 }} />

          {courses.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{
                flex: 1,
                minHeight: 0,
                padding: "50px 20px",
                color: "#77718b",
              }}
            >
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#403b55",
                }}
              >
                No courses available
              </div>

              <small style={{ marginTop: "5px" }}>
                There are currently no courses to display.
              </small>
            </div>
          ) : (
            <>
              {/* ================= TABLE ================= */}
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  backgroundColor: "#fff",
                  borderRadius: "14px",
                  border: "1px solid #eeeaf8",
                  boxShadow: "0 2px 10px rgba(48,45,69,0.05)",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    overflow: "auto",
                  }}
                >
                  <table
                    style={{
                      minWidth: "1100px",
                      width: "100%",
                      tableLayout: "auto",
                      background: "#fff",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                    }}
                  >
                    <thead>
                      <tr>
                        {columnKeys.map((key) => (
                          <th
                            key={key}
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 5,
                              minWidth: "140px",
                              whiteSpace: "nowrap",
                              background: "#d5d1fd",
                              color: "#302d45",
                              fontWeight: "600",
                              padding: "14px",
                              textAlign: "center",
                              letterSpacing: "0.4px",
                              fontSize: "13px",
                              borderBottom: "1px solid #e6e3f3",
                            }}
                          >
                            {key.toUpperCase()}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {courses.map((course) => (
                        <tr
                          key={course.id}
                          style={{
                            borderBottom: "1px solid #f0eef5",
                          }}
                        >
                          {/* ID */}
                          <td
                            style={{
                              whiteSpace: "pre-line",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              textAlign: "center",
                            }}
                          >
                            {course.id}
                          </td>

                          {/* TITLE */}
                          <td
                            style={{
                              minWidth: "320px",
                              whiteSpace: "pre-line",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <textarea
                                value={course.title}
                                readOnly={!canEdit}
                                onChange={(e) =>
                                  handleInputChange(
                                    course.id,
                                    "title",
                                    e.target.value,
                                  )
                                }
                                onInput={(e) => autoResizeTextarea(e.target)}
                                data-autoresize
                                rows={1}
                                style={{
                                  width: "100%",
                                  border: "1px solid transparent",
                                  outline: "none",
                                  background: "transparent",
                                  resize: "none",
                                  textAlign: "center",
                                  padding: "8px",
                                  borderRadius: "6px",
                                  cursor: canEdit ? "text" : "default",
                                }}
                              />

                              {canEdit && (
                                <button
                                  onClick={() =>
                                    handleEdit(course.id, "title", course.title)
                                  }
                                  disabled={!editStates[`${course.id}-title`]}
                                  className="btn"
                                  style={{
                                    backgroundColor: "#bff1db",
                                    border: "none",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    padding: "5px 13px",
                                    borderRadius: "6px",
                                  }}
                                >
                                  Save
                                </button>
                              )}
                            </div>
                          </td>

                          {/* SESSION */}
                          <td
                            style={{
                              whiteSpace: "pre-line",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              textAlign: "center",
                            }}
                          >
                            {canEdit &&
                            (course.sessionId == null ||
                              editStates[`${course.id}-sessionId`]) ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <select
                                  value={course.sessionId ?? ""}
                                  onChange={(e) =>
                                    handleInputChange(
                                      course.id,
                                      "sessionId",
                                      e.target.value,
                                    )
                                  }
                                  className="form-select"
                                  style={{
                                    minWidth: "145px",
                                    fontSize: "13px",
                                    border: "1px solid #e5e1ef",
                                    borderRadius: "7px",
                                  }}
                                >
                                  <option value="" disabled>
                                    Set Session
                                  </option>

                                  {availableSessions.map((session) => (
                                    <option
                                      key={session.value}
                                      value={session.value}
                                    >
                                      {session.label}
                                    </option>
                                  ))}
                                </select>

                                <button
                                  onClick={() =>
                                    handleEdit(
                                      course.id,
                                      "sessionId",
                                      course.sessionId,
                                    )
                                  }
                                  disabled={
                                    !course.sessionId ||
                                    !editStates[`${course.id}-sessionId`]
                                  }
                                  className="btn"
                                  style={{
                                    backgroundColor: "#bff1db",
                                    border: "none",
                                    color: "#355c4a",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    padding: "5px 13px",
                                    borderRadius: "6px",
                                  }}
                                >
                                  Save
                                </button>
                              </div>
                            ) : (
                              <span>
                                {availableSessions.find(
                                  (session) =>
                                    String(session.value) ===
                                    String(course.sessionId),
                                )?.label || course.sessionId}
                              </span>
                            )}
                          </td>

                          {/* YEAR */}
                          <td
                            style={{
                              whiteSpace: "pre-line",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              textAlign: "center",
                            }}
                          >
                            {canEdit &&
                            (course.year == null ||
                              editStates[`${course.id}-year`]) ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <input
                                  type="number"
                                  min="2026"
                                  max="9999"
                                  value={course.year ?? ""}
                                  placeholder="Set Year"
                                  onChange={(e) =>
                                    handleInputChange(
                                      course.id,
                                      "year",
                                      e.target.value,
                                    )
                                  }
                                  className="form-control"
                                  style={{
                                    width: "100%",
                                    maxWidth: "100px",
                                    textAlign: "center",
                                    border: "1px solid #e5e1ef",
                                    borderRadius: "7px",
                                    fontSize: "13px",
                                  }}
                                />

                                <button
                                  onClick={() =>
                                    handleEdit(course.id, "year", course.year)
                                  }
                                  disabled={
                                    !course.year ||
                                    !editStates[`${course.id}-year`]
                                  }
                                  className="btn"
                                  style={{
                                    backgroundColor: "#bff1db",
                                    border: "none",
                                    color: "#355c4a",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    padding: "5px 13px",
                                    borderRadius: "6px",
                                  }}
                                >
                                  Save
                                </button>
                              </div>
                            ) : (
                              <span>{course.year}</span>
                            )}
                          </td>

                          {/* TEACHER */}
                          <td
                            style={{
                              whiteSpace: "pre-line",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              textAlign: "center",
                            }}
                          >
                            {course.Teacher?.name}
                          </td>

                          {/* CLASS TIMING */}
                          <td
                            style={{
                              whiteSpace: "pre-line",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              textAlign: "center",
                              overflow: "visible",
                            }}
                          >
                            <ClassTimingEditor
                              courseId={course.id}
                              value={course.classTime}
                              handleEdit={handleEdit}
                              notificationCtx={notificationCtx}
                              canEdit={canEdit}
                            />
                          </td>

                          {/* START DATE */}
                          <td
                            style={{
                              whiteSpace: "pre-line",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <input
                                type="date"
                                value={course.startDate.split("T")[0]}
                                readOnly={!canEdit}
                                disabled={!canEdit}
                                onChange={(e) =>
                                  handleInputChange(
                                    course.id,
                                    "startDate",
                                    e.target.value,
                                  )
                                }
                                className="form-control"
                                style={{
                                  width: "100%",
                                  maxWidth: "170px",
                                  border: "1px solid #e5e1ef",
                                  borderRadius: "7px",
                                  fontSize: "13px",
                                  cursor: canEdit ? "text" : "default",
                                }}
                              />

                              {canEdit && (
                                <button
                                  onClick={() =>
                                    handleEdit(
                                      course.id,
                                      "startDate",
                                      course.startDate,
                                    )
                                  }
                                  disabled={!editStates[`${course.id}-startDate`]}
                                  className="btn"
                                  style={{
                                    backgroundColor: "#bff1db",
                                    border: "none",
                                    color: "#355c4a",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    padding: "5px 13px",
                                    borderRadius: "6px",
                                  }}
                                >
                                  Save
                                </button>
                              )}
                            </div>
                          </td>

                          {/* FEE */}
                          <td
                            style={{
                              whiteSpace: "pre-line",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <input
                                type="text"
                                value={course.fee}
                                readOnly={!canEdit}
                                onChange={(e) =>
                                  handleInputChange(
                                    course.id,
                                    "fee",
                                    e.target.value,
                                  )
                                }
                                className="form-control"
                                style={{
                                  width: "100%",
                                  maxWidth: "140px",
                                  textAlign: "center",
                                  border: "1px solid #e5e1ef",
                                  borderRadius: "7px",
                                  cursor: canEdit ? "text" : "default",
                                }}
                              />

                              {canEdit && (
                                <button
                                  onClick={() =>
                                    handleEdit(course.id, "fee", course.fee)
                                  }
                                  disabled={!editStates[`${course.id}-fee`]}
                                  className="btn"
                                  style={{
                                    backgroundColor: "#bff1db",
                                    border: "none",
                                    color: "#355c4a",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    padding: "5px 13px",
                                    borderRadius: "6px",
                                  }}
                                >
                                  Save
                                </button>
                              )}
                            </div>
                          </td>

                          {/* DURATION */}
                          <td
                            style={{
                              whiteSpace: "pre-line",
                              fontSize: "13px",
                              lineHeight: "1.5",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              <input
                                type="text"
                                value={course.duration}
                                readOnly={!canEdit}
                                onChange={(e) =>
                                  handleInputChange(
                                    course.id,
                                    "duration",
                                    e.target.value,
                                  )
                                }
                                className="form-control"
                                style={{
                                  width: "100%",
                                  maxWidth: "140px",
                                  textAlign: "center",
                                  border: "1px solid #e5e1ef",
                                  borderRadius: "7px",
                                  cursor: canEdit ? "text" : "default",
                                }}
                              />

                              {canEdit && (
                                <button
                                  onClick={() =>
                                    handleEdit(
                                      course.id,
                                      "duration",
                                      course.duration,
                                    )
                                  }
                                  disabled={!editStates[`${course.id}-duration`]}
                                  className="btn"
                                  style={{
                                    backgroundColor: "#bff1db",
                                    border: "none",
                                    color: "#355c4a",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    padding: "5px 13px",
                                    borderRadius: "6px",
                                  }}
                                >
                                  Save
                                </button>
                              )}
                            </div>
                          </td>

                          {/* ACTIONS */}
                          {canEdit && (
                            <td
                              style={{
                                padding: "12px 14px",
                                textAlign: "center",
                                verticalAlign: "middle",
                                width: "1%",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <div
                                style={{
                                  display: "inline-flex",
                                  flexDirection: "column",
                                  gap: "10px",
                                  alignItems: "center",
                                  minWidth: "250px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    alignItems: "stretch",
                                    width: "100%",
                                  }}
                                >
                                  <button
                                    className="btn"
                                    onClick={() => {
                                      notificationCtx.show({
                                        message:
                                          "Are you sure you want to " +
                                          (course.active
                                            ? "deactivate"
                                            : "activate") +
                                          " this course? All registrations for this course that are " +
                                          (course.active
                                            ? "enrolled/pending"
                                            : "unenrolled") +
                                          " will be " +
                                          (course.active
                                            ? "unenrolled"
                                            : "enrolled") +
                                          ".",
                                        onConfirm: () =>
                                          handleEdit(
                                            course.id,
                                            "active",
                                            !course.active,
                                          ),
                                        stat: "info",
                                      });
                                    }}
                                    style={{
                                      flex: 1,
                                      border: "none",
                                      fontWeight: "600",
                                      fontSize: "12px",
                                      padding: "7px 12px",
                                      borderRadius: "7px",
                                      whiteSpace: "nowrap",
                                      backgroundColor: course.active
                                        ? "#f2c4c4"
                                        : "#bff1db",
                                      color: course.active
                                        ? "#654343"
                                        : "#355c4a",
                                    }}
                                  >
                                    {course.active ? "Deactivate" : "Activate"}
                                  </button>

                                  <button
                                    className="btn"
                                    onClick={() => {
                                      const currentlyListed =
                                        course.publicListed !== false;
                                      notificationCtx.show({
                                        message: currentlyListed
                                          ? "Hide this course from the student register page and class schedules? Existing enrollments and admin tools are unaffected."
                                          : "Show this course on the student register page and class schedules?",
                                        onConfirm: () =>
                                          handleEdit(
                                            course.id,
                                            "publicListed",
                                            !currentlyListed,
                                          ),
                                        stat: "info",
                                      });
                                    }}
                                    style={{
                                      flex: 1,
                                      border: "none",
                                      fontWeight: "600",
                                      fontSize: "12px",
                                      padding: "7px 12px",
                                      borderRadius: "7px",
                                      whiteSpace: "nowrap",
                                      backgroundColor:
                                        course.publicListed !== false
                                          ? "#d5d1fd"
                                          : "#f2e6c9",
                                      color:
                                        course.publicListed !== false
                                          ? "#403b55"
                                          : "#654343",
                                    }}
                                  >
                                    {course.publicListed !== false
                                      ? "Hide from catalog"
                                      : "Show in catalog"}
                                  </button>
                                </div>

                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    alignItems: "stretch",
                                    width: "100%",
                                  }}
                                >
                                  <button
                                    className="btn"
                                    onClick={() => {
                                      const currentlyEnabled = Boolean(
                                        course.attendanceEnabled,
                                      );
                                      notificationCtx.show({
                                        message: currentlyEnabled
                                          ? "Disable attendance for this course? Existing QR tokens are kept; students will no longer see Attendance QR."
                                          : "Enable attendance for this course? Existing enrollments will receive attendance QR tokens.",
                                        onConfirm: () =>
                                          handleEdit(
                                            course.id,
                                            "attendanceEnabled",
                                            !currentlyEnabled,
                                          ),
                                        stat: "info",
                                      });
                                    }}
                                    style={{
                                      flex: 1,
                                      border: "none",
                                      fontWeight: "600",
                                      fontSize: "12px",
                                      padding: "7px 12px",
                                      borderRadius: "7px",
                                      whiteSpace: "nowrap",
                                      backgroundColor: course.attendanceEnabled
                                        ? "#bff1db"
                                        : "#f4f1fa",
                                      color: course.attendanceEnabled
                                        ? "#355c4a"
                                        : "#514b65",
                                    }}
                                  >
                                    {course.attendanceEnabled
                                      ? "Attendance on"
                                      : "Enable attendance"}
                                  </button>

                                  <button
                                    className="btn"
                                    onClick={() => handleDeleteCourse(course)}
                                    style={{
                                      flex: 1,
                                      backgroundColor: "#f9e1be",
                                      border: "none",
                                      borderRadius: "7px",
                                      fontWeight: "600",
                                      fontSize: "12px",
                                      padding: "7px 12px",
                                      color: "#654343",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ================= FIXED FOOTER ================= */}
              <PaginationFooter
                page={currentPage}
                limit={limit}
                total={total}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemLabel="courses"
                disabled={loading}
              />
            </>
          )}
          {canEdit && showform && (
            <div
              className="modal show"
              style={{
                display: "block",
                backgroundColor: "rgba(48,45,69,0.55)",
              }}
              tabIndex="-1"
            >
              <div
                className="modal-dialog modal-dialog-centered modal-lg"
                style={{
                  maxHeight: "100vh",
                  display: "flex",
                  flexDirection: "column",
                  margin: "20px auto",
                }}
              >
                <div
                  className="modal-content"
                  style={{
                    flexGrow: 1,
                    backgroundColor: "#fff",
                    border: "none",
                    borderRadius: "18px",
                    overflow: "hidden",
                    boxShadow: "0 12px 40px rgba(48,45,69,0.18)",
                  }}
                >
                  {/* ================= HEADER ================= */}
                  <div
                    style={{
                      backgroundColor: "#d5d1fd",
                      padding: "22px 28px",
                      position: "relative",
                      flexShrink: 0,
                    }}
                  >
                    <div>
                      <h3
                        className="mb-1"
                        style={{
                          fontWeight: "700",
                          color: "#302d45",
                          fontSize: "22px",
                        }}
                      >
                        ADD COURSE
                      </h3>

                      <small
                        style={{
                          color: "#625d75",
                        }}
                      >
                        Create a new course and configure its schedule
                      </small>
                    </div>

                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setshowform(false)}
                      style={{
                        position: "absolute",
                        top: "22px",
                        right: "24px",
                      }}
                      aria-label="Close"
                    />
                  </div>

                  {/* ================= FORM BODY ================= */}
                  <div
                    className="modal-body"
                    style={{
                      overflowY: "auto",
                      padding: "28px 32px",
                      maxHeight: "calc(109vh - 60px)",
                    }}
                  >
                    <form onSubmit={handleSubmit}>
                      {/* ================= COURSE DETAILS ================= */}
                      <div
                        style={{
                          marginBottom: "28px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: "700",
                            color: "#403b55",
                            marginBottom: "14px",
                          }}
                        >
                          Course Details
                        </div>

                        {/* Course Title */}
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label
                              className="form-label"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#514b65",
                              }}
                            >
                              Grade
                            </label>

                            <select
                              name="grade"
                              required
                              className="form-select"
                              value={selectedGrade}
                              onChange={(e) => setSelectedGrade(e.target.value)}
                              style={{
                                borderRadius: "8px",
                              }}
                            >
                              <option value="" disabled>
                                Select Grade
                              </option>

                              {availableGrades.map((grade) => (
                                <option key={grade.value} value={grade.value}>
                                  {grade.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="col-md-6">
                            <label
                              className="form-label"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#514b65",
                              }}
                            >
                              Academic Year
                            </label>

                            <input
                              type="number"
                              name="year"
                              min="2026"
                              max="9999"
                              value={course.year}
                              onChange={handleChange}
                              required
                              className="form-control"
                              style={{ borderRadius: "8px" }}
                            />
                          </div>

                          <div className="col-md-6">
                            <label
                              className="form-label"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#514b65",
                              }}
                            >
                              Subject
                            </label>

                            <select
                              name="subject"
                              required
                              className="form-select"
                              value={selectedSubject}
                              onChange={(e) =>
                                setSelectedSubject(e.target.value)
                              }
                              style={{
                                borderRadius: "8px",
                              }}
                            >
                              <option value="" disabled>
                                Select Subject
                              </option>

                              {availableSubjects.map((subject) => (
                                <option
                                  key={subject.value}
                                  value={subject.value}
                                >
                                  {subject.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Session + Teacher */}
                        <div className="row g-3 mt-1">
                          <div className="col-md-6">
                            <label
                              className="form-label"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#514b65",
                              }}
                            >
                              Session
                            </label>

                            <select
                              name="sessionId"
                              value={course.sessionId}
                              onChange={handleChange}
                              required
                              className="form-select"
                              style={{
                                borderRadius: "8px",
                              }}
                            >
                              <option value="" disabled>
                                Select Session
                              </option>

                              {availableSessions.map((session) => (
                                <option
                                  key={session.value}
                                  value={session.value}
                                >
                                  {session.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="col-md-6">
                            <label
                              className="form-label"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#514b65",
                              }}
                            >
                              Teacher
                            </label>

                            <select
                              name="teacherId"
                              value={course.teacherId}
                              onChange={handleChange}
                              required
                              className="form-select"
                              style={{
                                borderRadius: "8px",
                              }}
                            >
                              <option value="" disabled>
                                Select Teacher
                              </option>

                              {teachers.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Date + Duration */}
                        <div className="row g-3 mt-1">
                          <div className="col-md-6">
                            <label
                              className="form-label"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#514b65",
                              }}
                            >
                              Starting Date
                            </label>

                            <input
                              type="date"
                              name="startDate"
                              value={course.startDate}
                              onChange={handleChange}
                              required
                              className="form-control"
                              style={{
                                borderRadius: "8px",
                              }}
                            />
                          </div>

                          <div className="col-md-6">
                            <label
                              className="form-label"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#514b65",
                              }}
                            >
                              Duration
                            </label>

                            <select
                              name="duration"
                              value={course.duration}
                              onChange={handleChange}
                              required
                              className="form-select"
                              style={{
                                borderRadius: "8px",
                              }}
                            >
                              <option value="" disabled>
                                Select Duration
                              </option>

                              {availableDurations.map((duration) => (
                                <option
                                  key={duration.value}
                                  value={duration.value}
                                >
                                  {duration.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Fee */}
                        <div className="mt-3">
                          <label
                            className="form-label"
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#514b65",
                            }}
                          >
                            Course Fee
                          </label>

                          <input
                            type="number"
                            name="fee"
                            value={course.fee}
                            onChange={handleChange}
                            required
                            className="form-control"
                            placeholder="Enter course fee"
                            style={{
                              borderRadius: "8px",
                              maxWidth: "100%",
                            }}
                          />
                        </div>

                        {/* Catalog visibility */}
                        <div className="mt-3 form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="publicListed"
                            checked={course.publicListed !== false}
                            onChange={(e) =>
                              setCourse({
                                ...course,
                                publicListed: e.target.checked,
                              })
                            }
                          />
                          <div>
                            <label
                              className="form-check-label"
                              htmlFor="publicListed"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#514b65",
                              }}
                            >
                              Show in catalog
                            </label>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#77718b",
                                marginTop: "4px",
                              }}
                            >
                              When unchecked, the course stays off the student
                              register page and class schedules (offline only).
                            </div>
                          </div>
                        </div>

                        {/* Attendance module */}
                        <div className="mt-3 form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="attendanceEnabled"
                            checked={Boolean(course.attendanceEnabled)}
                            onChange={(e) =>
                              setCourse({
                                ...course,
                                attendanceEnabled: e.target.checked,
                              })
                            }
                          />
                          <div>
                            <label
                              className="form-check-label"
                              htmlFor="attendanceEnabled"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#514b65",
                              }}
                            >
                              Enable attendance
                            </label>
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#77718b",
                                marginTop: "4px",
                              }}
                            >
                              Students get an Attendance QR in My Courses.
                              Independent of LMS and catalog visibility.
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ================= SCHEDULE ================= */}
                      <div
                        style={{
                          borderTop: "1px solid #eeeaf5",
                          paddingTop: "24px",
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <div
                              style={{
                                fontSize: "15px",
                                fontWeight: "700",
                                color: "#403b55",
                              }}
                            >
                              Class Schedule
                            </div>

                            <small
                              style={{
                                color: "#77718b",
                              }}
                            >
                              Select the days and timings for each class.
                            </small>
                          </div>

                          <button
                            type="button"
                            className="btn"
                            onClick={addScheduleGroup}
                            style={{
                              backgroundColor: "#d5d1fd",
                              border: "none",
                              color: "#403b55",
                              fontWeight: "600",
                              fontSize: "13px",
                              padding: "8px 14px",
                              borderRadius: "8px",
                            }}
                          >
                            + Add Schedule
                          </button>
                        </div>

                        {scheduleGroups.map((group, index) => (
                          <div
                            key={index}
                            style={{
                              backgroundColor: "#faf9fd",
                              border: "1px solid #eeeaf5",
                              borderRadius: "12px",
                              padding: "18px",
                              marginBottom: "14px",
                            }}
                          >
                            {/* Schedule header */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div
                                style={{
                                  fontWeight: "600",
                                  color: "#403b55",
                                  fontSize: "14px",
                                }}
                              >
                                Schedule #{index + 1}
                              </div>

                              {scheduleGroups.length > 1 && (
                                <button
                                  type="button"
                                  className="btn"
                                  onClick={() => removeScheduleGroup(index)}
                                  style={{
                                    backgroundColor: "#f2c4c4",
                                    border: "none",
                                    color: "#654343",
                                    fontWeight: "600",
                                    fontSize: "12px",
                                    padding: "5px 10px",
                                    borderRadius: "7px",
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>

                            {/* Days */}
                            <div
                              style={{
                                marginBottom: "16px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: "#625d75",
                                  marginBottom: "8px",
                                }}
                              >
                                Class Days
                              </div>

                              <div className="d-flex flex-wrap gap-2">
                                {WEEK_DAYS.map((day) => {
                                  const active = group.days.includes(day.label);

                                  return (
                                    <button
                                      key={day.label}
                                      type="button"
                                      onClick={() =>
                                        toggleGroupDay(index, day.label)
                                      }
                                      className="btn"
                                      style={{
                                        backgroundColor: active
                                          ? "#d5d1fd"
                                          : "#fff",
                                        border: active
                                          ? "1px solid #d5d1fd"
                                          : "1px solid #ddd9e8",
                                        color: active ? "#403b55" : "#77718b",
                                        fontWeight: "600",
                                        fontSize: "12px",
                                        padding: "7px 12px",
                                        borderRadius: "20px",
                                      }}
                                    >
                                      {day.full}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Times */}
                            <div className="row g-3">
                              <div className="col-md-6">
                                <label
                                  className="form-label"
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    color: "#625d75",
                                  }}
                                >
                                  Start Time
                                </label>

                                <input
                                  type="time"
                                  className="form-control"
                                  value={group.startTime}
                                  onChange={(e) =>
                                    updateGroupField(
                                      index,
                                      "startTime",
                                      e.target.value,
                                    )
                                  }
                                  style={{
                                    borderRadius: "8px",
                                    backgroundColor: "#fff",
                                  }}
                                />
                              </div>

                              <div className="col-md-6">
                                <label
                                  className="form-label"
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    color: "#625d75",
                                  }}
                                >
                                  End Time
                                </label>

                                <input
                                  type="time"
                                  className="form-control"
                                  value={group.endTime}
                                  onChange={(e) =>
                                    updateGroupField(
                                      index,
                                      "endTime",
                                      e.target.value,
                                    )
                                  }
                                  style={{
                                    borderRadius: "8px",
                                    backgroundColor: "#fff",
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* ================= SUBMIT ================= */}
                      <div
                        style={{
                          borderTop: "1px solid #eeeaf5",
                          marginTop: "24px",
                          paddingTop: "20px",
                        }}
                      >
                        <button
                          type="submit"
                          className="btn w-100"
                          disabled={busy}
                          style={{
                            backgroundColor: "#bff1db",
                            border: "none",
                            color: "#355c4a",
                            fontWeight: "700",
                            padding: "11px 20px",
                            borderRadius: "9px",
                          }}
                        >
                          {busy ? "Working..." : "ADD COURSE"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
